import { CookieType, JWTCookieType } from './Types';
import { IUserSession } from './interfaces';
import decode from 'jwt-decode';

export class Cookie {
  /**
   * Helper method to create cookies on the client
   * 
   * @param name The name of the cookie we are saving
   * @param value The value this cooke should be
   * @param expires The data when this cookie should expire
   */
  public static set(name: CookieType, value: string, expires?: Date): void {
    if (Cookie.get(name) !== undefined) Cookie.clear(name);

    let cookie = `${name}=${value};path=/`;
    if (expires !== undefined) {
      cookie += `;expires=${expires.toUTCString()}`;
    }
    document.cookie = cookie;
  }

  /**
   * Helper method to create cookies on the client based on a JSON object
   * 
   * @param name The name of the cookie we are saving
   * @param value The value this cookie should be assigned to
   * @param expires The data when this cookie should expire
   */
  public static setJson<T>(name: CookieType, value: T, expires?: Date): void {
    if (value !== null && value !== undefined) {
      Cookie.set(name, JSON.stringify(value), expires);
    } else {
      Cookie.clear(name);
    }
  }

  /**
   * Helper method to get a cookie value from the browser
   * 
   * @param name The name of the cookie we are looking into creating
   */
  public static get(name: CookieType): string | undefined {
    const cookie = document.cookie.split(';').find(x => x.trim().startsWith(`${name}=`));
    if (cookie !== undefined) return cookie.replace(`${name}=`, '').replace(' path=/', '');
  }

  /**
   * Helper method to get the jwt from a cookie
   * 
   * @param name The name of the jwt token we are wanting to get
   */
  public static getJWT(name?: JWTCookieType): IUserSession | undefined {
    const token = Cookie.get(name ?? 'ApiSession');
    if (token !== undefined) {
      return decode(token);
    }
  }

  /**
   * Helper method to convert the cookie into a JSON object
   * 
   * @param name The name of the token we want to convert to JSON
   */
  public static getJson<T>(name?: CookieType): T | undefined {
    const token = Cookie.get(name ?? 'ApiSession');
    if (token !== undefined) {
      return JSON.parse(token);
    }
  }

  /**
   * Helper method that clears the cookie from the system
   * 
   * @param name The name of the cookie we want to clear
   */
  public static clear(name: CookieType): void {
    if (Cookie.get(name) !== undefined) {
      document.cookie = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT`;
    }
  }
}